/**
 * ClubGate — membership landing for gate-on tenants.
 *
 * Two sign-in modes:
 *   1. MEMBER  · card no + last name → POST /api/club/auth { card, last }
 *   2. ADMIN   · admin email + admin password → POST /api/club/auth { email, password }
 *               Verified against the `users` collection (same hashing as /admin/login);
 *               requires `concierge_admin` role. Synthesises an in-memory founder
 *               session — no `club_members` row is written.
 *
 * FIR-20260611-001
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import ClubLayout from "./ClubLayout";
import * as api from "./api";

// FIR-20260624-RememberMe · founder direction: when a member comes back
// to /club after their session has been cleared (browser cleared cookies,
// stale-session 401, signed out elsewhere, etc.) the sign-in form should
// pre-fill their card number + last name from the previous successful
// sign-in. This is intentionally SEPARATE from the active-session store
// (`club_member_session_v1` in ClubDirectory.jsx) — that one is wiped
// aggressively on 401/sign-out; this one persists until the user clicks
// "Forget me" on the gate.
const REMEMBER_KEY = "club_member_remember_v1";

function loadRemember() {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d && typeof d.card === "string" && typeof d.last === "string") return d;
    return null;
  } catch { return null; }
}
export function saveRemember(card, last) {
  try { localStorage.setItem(REMEMBER_KEY, JSON.stringify({ card, last })); } catch { /* ignore */ }
}
export function clearRemember() {
  try { localStorage.removeItem(REMEMBER_KEY); } catch { /* ignore */ }
}

export default function ClubGate({ tenantName, onAuthed, allowSignup = false }) {
  // FIR-20260623 · founder direction: remove the public "Sign in as admin"
  // link from the member-facing gate. Admins still reach the admin form by
  // appending `?as=admin` to the /club URL (or via /mira/console/login).
  const initialMode = (() => {
    try {
      const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
      const asParam = (params.get("as") || "").toLowerCase();
      return asParam === "admin" ? "admin" : "member";
    } catch {
      return "member";
    }
  })();
  const [mode, setMode] = useState(initialMode);
  // Prefill from the persistent remember-me store.
  const remembered = loadRemember();
  const [card, setCard] = useState(remembered?.card || "");
  const [last, setLast] = useState(remembered?.last || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function forgetMe() {
    clearRemember();
    setCard("");
    setLast("");
  }

  async function submit(e) {
    e.preventDefault();
    setError(null);

    if (mode === "admin") {
      if (!email.trim() || !password) {
        setError("Please enter your admin email and password.");
        return;
      }
      setSubmitting(true);
      try {
        const r = await api.authMember({ email: email.trim(), password });
        onAuthed(r.member);
      } catch (e2) {
        const code = e2.message || "";
        if (code === "invalid_admin_credentials") {
          setError("That email and password don't match an admin account.");
        } else if (code === "not_an_admin") {
          setError("This account doesn't have admin access.");
        } else {
          setError(code || "Couldn't sign you in. Please try again.");
        }
      } finally { setSubmitting(false); }
      return;
    }

    // Member sign-in
    if (!card.trim() || !last.trim()) {
      setError("Please enter both your membership card number and last name.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.authMember({ card: card.trim(), last: last.trim() });
      // FIR-20260624-RememberMe · persist the credentials so a future
      // gate render (after session loss / 401) auto-fills the form.
      saveRemember(card.trim(), last.trim());
      onAuthed(r.member);
    } catch (e2) {
      const code = e2.message || "";
      if (code === "member_not_authorised") {
        setError("Your membership isn't currently active. Please contact MindEscapes® to enable access.");
      } else if (code === "invalid_membership") {
        setError("That card number and last name don't match our records. Please check and try again.");
      } else {
        setError(code || "Couldn't sign you in. Please try again.");
      }
    } finally { setSubmitting(false); }
  }

  function switchMode(next) {
    setError(null);
    setMode(next);
  }

  return (
    <ClubLayout member={null}>
      <main className="wrap" style={{ paddingTop: 60, paddingBottom: 80 }}>
        <div style={{ maxWidth: 460, margin: "20px auto 0" }} data-testid="club-gate">
          <div className="kicker" data-testid="club-gate-kicker">MindEscapes<sup>®</sup> · {mode === "admin" ? "Admin access" : "Member access"}</div>
          <h1 className="serif" style={{ fontSize: "clamp(28px, 4.5vw, 44px)", marginTop: 8, lineHeight: 1.1 }}>
            Welcome to <em style={{ color: "var(--teal)", fontStyle: "italic" }}>MindEscapes</em><sup style={{ fontSize: "0.5em" }}>®</sup>
          </h1>
          <p style={{ color: "var(--muted)", marginTop: 12 }}>
            {mode === "admin"
              ? "Sign in with your admin email and password."
              : "Sign in with your membership card number and last name to access the Club Discovery Network."}
          </p>

          <form onSubmit={submit} style={{ marginTop: 28 }} data-testid={`club-gate-form-${mode}`}>
            {mode === "member" ? (
              <>
                <Field label="Membership card number *">
                  <input type="text" value={card} onChange={(e) => setCard(e.target.value)}
                         placeholder="e.g. KVB-1024"
                         autoComplete="off" autoCapitalize="characters"
                         style={inputStyle()} data-testid="club-gate-card" />
                </Field>
                <Field label="Last name *">
                  <input type="text" value={last} onChange={(e) => setLast(e.target.value)}
                         placeholder="As it appears on your membership"
                         autoComplete="family-name"
                         style={inputStyle()} data-testid="club-gate-last" />
                </Field>
              </>
            ) : (
              <>
                <Field label="Admin email *">
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                         placeholder="you@clubconcierge.in"
                         autoComplete="email"
                         style={inputStyle()} data-testid="club-gate-admin-email" />
                </Field>
                <Field label="Admin password *">
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                         placeholder="Your admin password"
                         autoComplete="current-password"
                         style={inputStyle()} data-testid="club-gate-admin-password" />
                </Field>
              </>
            )}

            {error && (
              <div data-testid="club-gate-error" style={{
                padding: "12px 14px", borderRadius: 10, marginBottom: 16, fontSize: 13.5,
                background: "rgba(180,68,31,0.12)", color: "#7a2c12",
                border: "1px solid rgba(180,68,31,0.3)",
              }}>{error}</div>
            )}

            <button type="submit" className="btn btn-primary"
                    disabled={submitting} style={{ width: "100%" }}
                    data-testid="club-gate-submit">
              {submitting ? "Signing in…" : "Continue"}
            </button>

            {/* FIR-20260624-RememberMe · "Forget me" link visible only
                when we have a stored remember-me entry AND we're on the
                member-sign-in mode (admins don't get auto-prefill). */}
            {mode === "member" && (card || last) && remembered && (
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button type="button" onClick={forgetMe}
                        data-testid="club-gate-forget-me"
                        style={{
                          background: "none", border: "none", padding: 0,
                          color: "var(--muted)", fontSize: 12.5,
                          textDecoration: "underline",
                          textUnderlineOffset: 3, cursor: "pointer",
                        }}>
                  Not you? Forget me
                </button>
              </div>
            )}
          </form>

          {/* Mode toggle · FIR-20260623 · founder direction: hide the
                "Sign in as admin →" link from the member-facing view.
                Admins reach this surface via ?as=admin URL param; once
                in admin mode we still surface the toggle BACK to member
                sign-in so the founder isn't trapped on the admin form. */}
          {mode === "admin" && (
            <div style={{ marginTop: 18, padding: "14px 16px", borderTop: "1px solid var(--line-2)",
                          textAlign: "center", fontSize: 13.5 }}>
              <button type="button" onClick={() => switchMode("member")}
                      data-testid="club-gate-mode-member"
                      style={modeToggleStyle()}>
                ← Sign in as member
              </button>
            </div>
          )}

          {allowSignup && mode === "member" && (
            <div style={{ marginTop: 6, textAlign: "center", fontSize: 13.5 }}
                 data-testid="club-gate-signup-cta">
              Don&apos;t have a membership yet?{" "}
              <Link to="/club/signup" data-testid="club-gate-signup-link"
                    style={{ color: "var(--teal-d)", fontWeight: 700, textDecoration: "underline", textUnderlineOffset: 3 }}>
                Apply for membership →
              </Link>
            </div>
          )}

          <div style={{ marginTop: 28, fontSize: 12.5, color: "var(--muted)", lineHeight: 1.6 }}>
            Access is reserved for eligible members.
            For help, contact <a href="mailto:enquiries@mindescapes.in" style={{ color: "var(--teal-d)", fontWeight: 600 }}>enquiries@mindescapes.in</a>.
          </div>
        </div>
      </main>
    </ClubLayout>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase",
                      color: "var(--muted)", fontWeight: 700, marginBottom: 6, display: "block" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function inputStyle() {
  return {
    width: "100%", height: 54,
    border: "1px solid var(--line)", borderRadius: 12,
    background: "var(--card)", padding: "0 16px",
    fontFamily: "'Archivo', sans-serif", fontSize: 16,
    color: "var(--ink)", boxShadow: "var(--shadow)",
    boxSizing: "border-box",
  };
}

function modeToggleStyle() {
  return {
    background: "transparent", border: "none", padding: 0, cursor: "pointer",
    color: "var(--teal-d)", fontWeight: 700, textDecoration: "underline",
    textUnderlineOffset: 3, fontSize: 13.5,
  };
}
